// RecipientResultList — the owner / vendor search results on the Request a W-9
// page (owner-sync spec §6). Rows arrive pre-built by lib/ownerSync.ts
// (ownerResultRow / vendorResultRow): the AppFolio id is the row's `key` —
// what the pick addresses — and the rest is a name, an email / city line and
// status badges. Nothing on screen is an id.

import type { RecipientResultRow } from '@/lib/ownerSync';

export function RecipientResultList({
  rows,
  selectedKey,
  onPick,
  emptyText,
}: {
  rows: RecipientResultRow[];
  selectedKey?: string | null | undefined;
  onPick: (key: string) => void;
  emptyText: string;
}): React.JSX.Element {
  if (rows.length === 0) {
    return <p className="oc-meta" style={{ margin: 0 }}>{emptyText}</p>;
  }
  return (
    <ul
      style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
      aria-label="Search results"
    >
      {rows.map((r) => {
        const selected = r.key === selectedKey;
        return (
          <li
            key={r.key}
            style={{
              border: `1px solid ${selected ? 'var(--action)' : 'var(--border)'}`,
              borderRadius: 8,
              padding: 'var(--space-3)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 16rem', minWidth: 0 }}>
              <div style={{ fontWeight: 'var(--weight-medium)' }}>{r.name}</div>
              {r.detail && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>{r.detail}</div>
              )}
              {r.badges.length > 0 && (
                <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap', marginTop: 'var(--space-1)' }}>
                  {r.badges.map((b) => (
                    <span key={b.text} className="pill" data-tone={b.tone} data-size="sm">
                      {b.text}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn"
              data-variant={selected ? 'primary' : 'secondary'}
              data-size="sm"
              aria-pressed={selected}
              onClick={() => onPick(r.key)}
            >
              {selected ? 'Selected' : 'Choose'}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
